import React, { useState } from 'react';
import { Loader2, Calendar, Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';
import { blackout } from '../services/api';
import { toast } from '../hooks/use-toast';
import { ModalShell, Field, SelectField, Textarea } from './BlackoutSubmitBusinessModal';

const CATEGORIES = ['Community', 'Business Networking', 'Music', 'Art & Culture',
  'Food & Drink', 'Education', 'Fitness & Sports', 'Family', 'Other'];

const EMPTY = {
  name: '', description: '', eventDate: '', startTime: '', endTime: '',
  venueName: '', address: '', city: '', state: '', zip: '',
  website: '', social: '', organizerName: '', organizerEmail: '', organizerPhone: '',
  ticketUrl: '', ticketPrice: '', category: '', eventImage: '', notes: '', sourceUrl: '',
};

const fireConfetti = () => {
  const burst = (opts) => confetti({ spread: 70, origin: { y: 0.6 }, particleCount: 70, ...opts });
  burst({ angle: 60, colors: ['#8b5cf6', '#fbbf24', '#f97316'] });
  burst({ angle: 120, colors: ['#8b5cf6', '#fbbf24', '#f97316'] });
  setTimeout(() => burst({ particleCount: 100, spread: 100, startVelocity: 55 }), 250);
};

export default function BlackoutSubmitEventModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: 'Event name is required' }); return; }
    if (!form.eventDate) { toast({ title: 'Event date is required' }); return; }
    setBusy(true);
    try {
      const res = await blackout.submitEvent(form);
      setResult(res);
      if (res.status === 'new') fireConfetti();
      onSuccess?.(res);
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setForm(EMPTY); setResult(null); onClose(); };

  if (result) {
    const palette = {
      new: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
      duplicate: 'border-amber-500/60 bg-amber-500/10 text-amber-200',
      pending_review: 'border-sky-500/60 bg-sky-500/10 text-sky-200',
      already_credited: 'border-neutral-500/40 bg-neutral-500/10 text-neutral-200',
    }[result.status] || 'border-neutral-500/40';
    return (
      <ModalShell onClose={close} title="Submission Result" testId="blackout-ev-result">
        <div className={`rounded-xl border-2 ${palette} p-6 text-center`}>
          <Trophy className="mx-auto mb-3 text-amber-300" size={40}/>
          <div className="text-xl font-black mb-2 uppercase tracking-wider">{result.message}</div>
          {result.pointsAwarded > 0 && (
            <div className="text-3xl font-black text-amber-300 mt-3" data-testid="blackout-ev-points-awarded">
              +{result.pointsAwarded} points
            </div>
          )}
          {result.flagReason && (
            <div className="mt-3 text-xs text-neutral-400">Flag: {result.flagReason.replace(/_/g,' ')}</div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => { setResult(null); setForm(EMPTY); }} data-testid="blackout-ev-submit-another"
            className="px-4 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-200 hover:border-emerald-500">
            Submit another
          </button>
          <button onClick={close} data-testid="blackout-ev-close-result"
            className="px-4 py-2 rounded-lg bg-emerald-500 text-black font-semibold text-sm">Done</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={close} title="Add an Event" icon={Calendar} testId="blackout-ev-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Event Name *" value={form.name} onChange={set('name')} testId="ev-name"/>
        <SelectField label="Category" value={form.category} onChange={set('category')} options={CATEGORIES} testId="ev-category"/>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">Event Date *</span>
          <input type="date" value={form.eventDate} onChange={set('eventDate')} data-testid="ev-date"
            className="mt-1 w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none"/>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Start Time" value={form.startTime} onChange={set('startTime')} testId="ev-start"/>
          <Field label="End Time" value={form.endTime} onChange={set('endTime')} testId="ev-end"/>
        </div>
        <Field label="Venue Name" value={form.venueName} onChange={set('venueName')} testId="ev-venue"/>
        <Field label="Address" value={form.address} onChange={set('address')} testId="ev-addr"/>
        <Field label="City" value={form.city} onChange={set('city')} testId="ev-city"/>
        <Field label="State" value={form.state} onChange={set('state')} testId="ev-state"/>
        <Field label="ZIP" value={form.zip} onChange={set('zip')} testId="ev-zip"/>
        <Field label="Event Website" value={form.website} onChange={set('website')} testId="ev-web"/>
        <Field label="Social Media" value={form.social} onChange={set('social')} testId="ev-social"/>
        <Field label="Organizer Name" value={form.organizerName} onChange={set('organizerName')} testId="ev-org-name"/>
        <Field label="Organizer Email" value={form.organizerEmail} onChange={set('organizerEmail')} testId="ev-org-email"/>
        <Field label="Organizer Phone" value={form.organizerPhone} onChange={set('organizerPhone')} testId="ev-org-phone"/>
        <Field label="Ticket URL" value={form.ticketUrl} onChange={set('ticketUrl')} testId="ev-ticket-url"/>
        <Field label="Ticket Price" value={form.ticketPrice} onChange={set('ticketPrice')} testId="ev-ticket-price"/>
        <Field label="Source URL" value={form.sourceUrl} onChange={set('sourceUrl')} testId="ev-source"/>
      </div>
      <Textarea label="Description" value={form.description} onChange={set('description')} testId="ev-desc"/>
      <Textarea label="Additional Notes" value={form.notes} onChange={set('notes')} testId="ev-notes"/>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-neutral-800">
        <button onClick={close} className="px-4 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-300">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="blackout-ev-submit"
          className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={14}/> : null}
          Submit Discovery
        </button>
      </div>
    </ModalShell>
  );
}
